import currency from "currency.js";
import { TFunction } from "i18next";
import {
  GetNotifications200Response,
  InstrumentTypeResponse,
  NotificationTypeResponse,
} from "../codegen-api";
import { getDefaultInstrumentType } from "../config/features";
import { AssetResponse } from "./asset";
import { getAssetShortName } from "./asset/assets";
import { getAssetFromSymbol } from "./instruments";
import { instrumentLabel } from "./instrumentLabel";
import { formatHumanPrice } from "./notifications.helpers";

export type FillPrecisionLookup = (
  asset?: AssetResponse,
  instrumentType?: InstrumentTypeResponse
) => { amount_precision?: number; price_precision?: number };

// Backstop for legacy notifications cached as raw 18-decimal big-ints.
// A real perp price/size will never exceed 1e10 in display units, so anything
// above that threshold is treated as 18-decimal fixed-point and scaled down.
const RAW_18DEC_THRESHOLD = 1e10;
function normalizeRawNumeric(raw: unknown): number {
  const n = Number(raw ?? 0);
  if (!Number.isFinite(n)) return 0;
  if (Math.abs(n) > RAW_18DEC_THRESHOLD) return n / 1e18;
  return n;
}

function formatFillPrice(raw: unknown, precision?: number): string {
  const n = normalizeRawNumeric(raw);
  if (n === 0) return "—";
  // Fall back to a price-magnitude heuristic if the symbol-specific
  // precision isn't available yet (markets list hasn't loaded).
  const p =
    precision !== undefined && precision >= 0
      ? precision
      : n >= 1000
      ? 2
      : n >= 1
      ? 4
      : 6;
  return currency(n, { precision: p }).format();
}

function formatFillAmount(raw: unknown, precision?: number): string {
  const n = normalizeRawNumeric(raw);
  if (n === 0) return "0";
  const max =
    precision !== undefined && precision >= 0 ? precision : 6;
  return n.toLocaleString(undefined, { maximumFractionDigits: max });
}

/**
 * The vertical a fill notification belongs to. The metadata carries the type
 * when the backend sends it; otherwise an option is recognised by its option
 * type, a perp by its "-PERP" name and a spot pair by its "-USDC" quote. With
 * nothing to go on, the build's default vertical -- not "perpetual", which
 * labelled every spot fill on a spot-only build "BTC-USD PERP".
 */
export function fillInstrumentType(
  metadata: Record<string, any>
): InstrumentTypeResponse {
  const declared = String(metadata.instrument_type || "").toUpperCase();
  if (
    (Object.values(InstrumentTypeResponse) as string[]).includes(declared)
  ) {
    return declared as InstrumentTypeResponse;
  }
  if (metadata.option_type) return InstrumentTypeResponse.Option;
  const name = String(
    metadata.instrument_name || metadata.instrument || ""
  ).toUpperCase();
  if (name.endsWith("-PERP")) return InstrumentTypeResponse.Perpetual;
  if (name.endsWith("-USDC")) return InstrumentTypeResponse.Spot;
  return getDefaultInstrumentType();
}

function formatFillInstrumentLabel(
  instrument: string | undefined,
  asset: string | undefined,
  instrumentType: InstrumentTypeResponse
): string {
  const normalizedInstrument = String(instrument || "").toUpperCase();
  const base = asset || normalizedInstrument.split("-")[0];
  const displayBase = getAssetShortName(base) || base;

  if (instrumentType === InstrumentTypeResponse.Perpetual && displayBase) {
    return `${displayBase}-USD PERP`;
  }
  if (instrumentType === InstrumentTypeResponse.Spot && displayBase) {
    return instrumentLabel(displayBase, InstrumentTypeResponse.Spot);
  }
  return normalizedInstrument || displayBase || "";
}

// Short human-readable summary for toast preview + browser push.
export function getNotificationSummary(
  notification: GetNotifications200Response,
  t: TFunction,
  getMarketPrecision?: FillPrecisionLookup
): { title: string; body: string } {
  const m = notification.metadata || {};
  switch (notification.notification_type) {
    case NotificationTypeResponse.Fill: {
      const instrumentType = fillInstrumentType(m);
      const instrument = m.instrument || "";
      const asset =
        (m.asset as AssetResponse | undefined) ||
        (typeof instrument === "string" && instrument
          ? getAssetFromSymbol(instrument)
          : undefined);
      // Spot and perp markets of one asset have different steps.
      const precision = getMarketPrecision?.(asset, instrumentType);
      const side = (m.side || "").toUpperCase();
      const amount = formatFillAmount(m.amount, precision?.amount_precision);
      const price = formatFillPrice(m.price, precision?.price_precision);
      return {
        title: t("notification_summary.fill_title", { defaultValue: "Order filled" }),
        body: t("notification_summary.fill_body", {
          defaultValue: "{{side}} {{amount}} {{instrument}} @ {{price}}",
          side,
          amount,
          instrument: formatFillInstrumentLabel(instrument, asset, instrumentType),
          price,
        }),
      };
    }
    case NotificationTypeResponse.LiquidationStarted: {
      const proximityRaw = (m as Record<string, unknown>).proximity;
      const proximity =
        typeof proximityRaw === "string" || typeof proximityRaw === "number"
          ? `${proximityRaw}%`
          : undefined;
      return {
        title: t("notification_summary.liquidation_started_title", {
          defaultValue: "Margin call",
        }),
        body: proximity
          ? t("notification_summary.liquidation_started_body_pct", {
              defaultValue:
                "You're {{proximity}} of the way to liquidation. Add margin to avoid it.",
              proximity,
            })
          : t("notification_summary.liquidation_started_body", {
              defaultValue:
                "Your account is approaching liquidation. Add margin to avoid it.",
            }),
      };
    }
    case NotificationTypeResponse.LiquidationCompleted:
      return {
        title: t("notification_summary.liquidation_completed_title", {
          defaultValue: "Liquidation completed",
        }),
        body: t("notification_summary.liquidation_completed_body", {
          defaultValue: "Position liquidation finished.",
        }),
      };
    case NotificationTypeResponse.DepositFinalized:
      return {
        title: t("notification_summary.deposit_title", { defaultValue: "Deposit confirmed" }),
        body: t("notification_summary.deposit_body", {
          defaultValue: "Your deposit has been credited.",
        }),
      };
    case NotificationTypeResponse.WithdrawalFinalized:
      return {
        title: t("notification_summary.withdrawal_title", {
          defaultValue: "Withdrawal complete",
        }),
        body: t("notification_summary.withdrawal_body", {
          defaultValue: "Funds have been sent to your wallet.",
        }),
      };
    case NotificationTypeResponse.MmpTriggered:
      return {
        title: t("notification_summary.mmp_triggered_title", {
          defaultValue: "MMP triggered",
        }),
        body: t("notification_summary.mmp_triggered_body", {
          defaultValue: "Market maker protection has frozen your orders.",
        }),
      };
    case NotificationTypeResponse.MmpUnfrozen:
      return {
        title: t("notification_summary.mmp_unfrozen_title", {
          defaultValue: "MMP unfrozen",
        }),
        body: t("notification_summary.mmp_unfrozen_body", {
          defaultValue: "Trading has resumed.",
        }),
      };
    case NotificationTypeResponse.MmpUpdated:
      return {
        title: t("notification_summary.mmp_updated_title", {
          defaultValue: "MMP updated",
        }),
        body: t("notification_summary.mmp_updated_body", {
          defaultValue: "Your market maker protection settings changed.",
        }),
      };
    case NotificationTypeResponse.Receive:
      return {
        title: t("notification_summary.receive_title", {
          defaultValue: "Funds received",
        }),
        body: t("notification_summary.receive_body", {
          defaultValue: "An inbound transfer was credited to your account.",
        }),
      };
    case NotificationTypeResponse.SendInitiated:
      return {
        title: t("notification_summary.send_initiated_title", {
          defaultValue: "Transfer initiated",
        }),
        body: t("notification_summary.send_initiated_body", {
          defaultValue: "Your outbound transfer is being processed.",
        }),
      };
    case NotificationTypeResponse.SendFinalized:
      return {
        title: t("notification_summary.send_finalized_title", {
          defaultValue: "Transfer sent",
        }),
        body: t("notification_summary.send_finalized_body", {
          defaultValue: "Your outbound transfer has completed.",
        }),
      };
    case NotificationTypeResponse.Referral:
      return {
        title: t("notification_summary.referral_title", {
          defaultValue: "Referral reward",
        }),
        body: t("notification_summary.referral_body", {
          defaultValue: "You earned a reward from a referral.",
        }),
      };
    case NotificationTypeResponse.Referee:
      return {
        title: t("notification_summary.referee_title", {
          defaultValue: "Referral applied",
        }),
        body: t("notification_summary.referee_body", {
          defaultValue: "A referral code has been applied to your account.",
        }),
      };
    case NotificationTypeResponse.Settled:
      return {
        title: t("notification_summary.settled_title", { defaultValue: "Position settled" }),
        body: t("notification_summary.settled_body", {
          defaultValue: "A position was settled.",
        }),
      };
    case NotificationTypeResponse.EmailVerification:
      return {
        title: t("notification_summary.email_title", { defaultValue: "Verify your email" }),
        body: t("notification_summary.email_body", {
          defaultValue: "Click the link we sent to confirm your address.",
        }),
      };
    // TP/SL outcomes. Prices are HUMAN decimal strings from the backend — used
    // verbatim, never scaled (see NotificationPanel/helpers.formatHumanDecimal).
    case NotificationTypeResponse.TpslExecuted:
    case NotificationTypeResponse.TpslRejected: {
      const executed =
        notification.notification_type === NotificationTypeResponse.TpslExecuted;
      const isTakeProfit = m.trigger_type === "take_profit";
      // TP/SL closes a position -- a perp unless the metadata says otherwise;
      // with no name, label it like every other market label.
      const instrument =
        m.instrument_name ||
        m.instrument ||
        instrumentLabel(
          m.asset,
          (m as Record<string, any>).instrument_type ||
            InstrumentTypeResponse.Perpetual
        );
      const isLong = typeof m.is_long === "boolean" ? m.is_long : m.side !== "sell";
      const direction = isLong ? "long" : "short";
      const trigger = formatHumanPrice(m.trigger_price) ?? "-";
      const price = formatHumanPrice(m.executed_price) ?? undefined;

      const title = isTakeProfit
        ? t(
            executed
              ? "notification_summary.tpsl_tp_executed_title"
              : "notification_summary.tpsl_tp_failed_title",
            {
              defaultValue: executed
                ? "Take Profit triggered"
                : "Take Profit failed",
            }
          )
        : t(
            executed
              ? "notification_summary.tpsl_sl_executed_title"
              : "notification_summary.tpsl_sl_failed_title",
            {
              defaultValue: executed
                ? "Stop Loss triggered"
                : "Stop Loss failed",
            }
          );

      if (!executed) {
        return {
          title,
          body: t("notification_summary.tpsl_rejected_body", {
            defaultValue:
              "{{instrument}} {{direction}} close failed (trigger {{trigger}})",
            instrument,
            direction,
            trigger,
          }),
        };
      }

      return {
        title,
        body: price
          ? t("notification_summary.tpsl_executed_body", {
              defaultValue:
                "{{instrument}} {{direction}} closed @ {{price}} (trigger {{trigger}})",
              instrument,
              direction,
              price,
              trigger,
            })
          : t("notification_summary.tpsl_executed_body_no_price", {
              defaultValue:
                "{{instrument}} {{direction}} closed (trigger {{trigger}})",
              instrument,
              direction,
              trigger,
            }),
      };
    }
    case NotificationTypeResponse.FarmBoostApplied:
      return {
        title: t("notification_summary.farm_boost_title", {
          defaultValue: "Boost applied",
        }),
        body: t("notification_summary.farm_boost_body", {
          defaultValue: "Your rewards boost is now active.",
        }),
      };
    default:
      return {
        title: t("notification_summary.default_title", { defaultValue: "Notification" }),
        body: t("notification_summary.default_body", {
          defaultValue: "You have a new notification.",
        }),
      };
  }
}
